import { createContext, useCallback, useContext, useRef, useState, ReactNode } from 'react';
import { CheckCircle2, XCircle, Info, X } from 'lucide-react';
import { registrarError } from '../services/telemetry';

type ToastKind = 'success' | 'error' | 'info';
type Toast = { id: number; kind: ToastKind; message: string };

type ToastApi = {
  toast: (message: string, kind?: ToastKind) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast debe usarse dentro de <ToastProvider>');
  return ctx;
}

const STYLE: Record<ToastKind, { icon: typeof Info; ring: string; tint: string; text: string }> = {
  success: { icon: CheckCircle2, ring: 'ring-emerald-500/30', tint: 'text-emerald-500', text: 'text-content' },
  error:   { icon: XCircle,      ring: 'ring-red-500/30',     tint: 'text-red-500',     text: 'text-content' },
  info:    { icon: Info,         ring: 'ring-brand-blue/30',  tint: 'text-brand-blue',  text: 'text-content' },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const remove = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const toast = useCallback((message: string, kind: ToastKind = 'info') => {
    const id = ++idRef.current;
    // Cada aviso rojo queda registrado (con usuario y pantalla) para saber dónde se atoran.
    if (kind === 'error') registrarError(message);
    setToasts((t) => [...t, { id, kind, message }]);
    setTimeout(() => remove(id), 4000);
  }, [remove]);

  const api: ToastApi = {
    toast,
    success: (m) => toast(m, 'success'),
    error: (m) => toast(m, 'error'),
    info: (m) => toast(m, 'info'),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Pila de toasts (abajo-derecha; en móvil ocupa ancho) */}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:bottom-4 sm:right-4 sm:items-end">
        {toasts.map((t) => {
          const s = STYLE[t.kind];
          const Icon = s.icon;
          return (
            <div
              key={t.id}
              className={`animate-toast-in pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border border-line bg-surface px-4 py-3 shadow-lg ring-1 ${s.ring}`}
              role="status"
            >
              <Icon size={18} className={`mt-0.5 shrink-0 ${s.tint}`} />
              <p className={`flex-1 text-sm font-medium ${s.text}`}>{t.message}</p>
              <button onClick={() => remove(t.id)} className="shrink-0 text-content-3 transition hover:text-content" aria-label="Cerrar">
                <X size={15} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
